import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import {
  formatPostalCode,
  isLink,
  logger,
  NON_UPPERCASE_LETTER_REGEX,
  POSTAL_CODE_REGEX,
} from 'shared/lib'
import { type IDropdownItem } from 'shared/ui'
import { PhoneInputStore } from 'shared/ui/PhoneWithLabelInput/store/phoneInputStore'
import { type INormalizedApiError } from 'shared/api'
import {
  ComplianceApi,
  type IResponseEinItem,
  type IResponseStockExchanges,
} from 'entities/Compliance'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type IRcsBusinessInfoStepParams, type IResponseRcsCompliance } from 'entities/Rcs'
import { type RcsWizardStore } from 'pages/settings/pages/rcsV2/store/rcsWizardStore'
import {
  BUSINESS_NAME_MAX_LENGTH,
  EIN_MAX_LENGTH,
  PUBLIC_CORPORATION_COMPANY_TYPE,
  US_COUNTRY_ISO_CODE,
} from 'pages/settings/pages/rcsV2/lib/constants'

const STOCK_SYMBOL_MAX_LENGTH = 10
const BUSINESS_NAME_MIN_LENGTH = 2
const STREET_ADDRESS_MIN_LENGTH = 2
const STREET_ADDRESS_MAX_LENGTH = 120
const STREET_ADDRESS_2_MAX_LENGTH = 30
const CITY_MAX_LENGTH = 60
const WEBSITE_URL_MAX_LENGTH = 2048

type IBusinessInfoTab = 'find' | 'manual'

export class BusinessInfoStore {
  private _ein = ''
  private _einError = ''
  private _businessName = ''
  private _businessNameError = ''
  private _streetAddress1 = ''
  private _streetAddress1Error = ''
  private _streetAddress2 = ''
  private _city = ''
  private _cityError = ''
  private _state = ''
  private _stateError = ''
  private _postalCode = ''
  private _postalCodeError = ''
  private _websiteUrl = ''
  private _websiteUrlError = ''
  private _companyType = ''
  private _companyTypeError = ''
  private _businessIndustry = ''
  private _businessIndustryError = ''
  private _stockExchange = ''
  private _stockExchangeError = ''
  private _stockExchanges: IResponseStockExchanges[] = []
  private _stockExchangesLoading = false
  private _stockSymbol = ''
  private _stockSymbolError = ''
  private _activeTab: IBusinessInfoTab = 'find'
  private _einItems: IResponseEinItem[] = []
  private _appliedEinItem: IResponseEinItem | null = null
  private _isNoEinResults = false
  private _statesLoading = false
  readonly phoneStore: PhoneInputStore

  constructor(private _wizardStore: RcsWizardStore, private _entities: IEntitiesStore) {
    this.phoneStore = new PhoneInputStore()

    makeAutoObservable(this)
    this.loadStates()
  }

  get ein() {
    return this._ein
  }

  get einError() {
    return this._einError
  }

  get einDisplayValue() {
    return this._entities.complianceStore.formatEinForDisplay(this._ein, US_COUNTRY_ISO_CODE)
  }

  get einPlaceholder() {
    return this._entities.complianceStore.getEinPlaceholder(US_COUNTRY_ISO_CODE)
  }

  get activeTab() {
    return this._activeTab
  }

  get einItems() {
    return this._einItems
  }

  get appliedEinItem() {
    return this._appliedEinItem
  }

  get isNoEinResults() {
    return this._isNoEinResults
  }

  get businessName() {
    return this._businessName
  }

  get businessNameError() {
    return this._businessNameError
  }

  get businessNameMaxLength() {
    return BUSINESS_NAME_MAX_LENGTH
  }

  get streetAddress1MaxLength() {
    return STREET_ADDRESS_MAX_LENGTH
  }

  get streetAddress2MaxLength() {
    return STREET_ADDRESS_2_MAX_LENGTH
  }

  get cityMaxLength() {
    return CITY_MAX_LENGTH
  }

  get websiteUrlMaxLength() {
    return WEBSITE_URL_MAX_LENGTH
  }

  get streetAddress1() {
    return this._streetAddress1
  }

  get streetAddress1Error() {
    return this._streetAddress1Error
  }

  get streetAddress2() {
    return this._streetAddress2
  }

  get city() {
    return this._city
  }

  get cityError() {
    return this._cityError
  }

  get state() {
    return this._state
  }

  get stateError() {
    return this._stateError
  }

  get stateOptions() {
    return this._entities.complianceStore.getStates(US_COUNTRY_ISO_CODE)
  }

  get statesLoading() {
    return this._statesLoading
  }

  get postalCode() {
    return this._postalCode
  }

  get postalCodeError() {
    return this._postalCodeError
  }

  get websiteUrl() {
    return this._websiteUrl
  }

  get websiteUrlError() {
    return this._websiteUrlError
  }

  get companyType() {
    return this._companyType
  }

  get companyTypeError() {
    return this._companyTypeError
  }

  get companyTypeOptions() {
    return this._wizardStore.companyTypeOptions
  }

  get businessIndustry() {
    return this._businessIndustry
  }

  get businessIndustryError() {
    return this._businessIndustryError
  }

  get businessIndustryOptions() {
    return this._wizardStore.businessIndustryOptions
  }

  get referencesLoading() {
    return this._wizardStore.businessReferencesLoading
  }

  get stockExchange() {
    return this._stockExchange
  }

  get stockExchangeError() {
    return this._stockExchangeError
  }

  get stockExchangeOptions(): IDropdownItem[] {
    return this._stockExchanges.map((exchange) => ({
      id: exchange.value,
      label: exchange.title,
    }))
  }

  get stockExchangesLoading() {
    return this._stockExchangesLoading
  }

  get stockSymbol() {
    return this._stockSymbol
  }

  get stockSymbolError() {
    return this._stockSymbolError
  }

  get addressCountry() {
    return US_COUNTRY_ISO_CODE
  }

  get isPublicCorporation() {
    return this._companyType === PUBLIC_CORPORATION_COMPANY_TYPE
  }

  private get _isEinValid() {
    return this._ein.length === EIN_MAX_LENGTH
  }

  private get _isBusinessNameValid() {
    return this._businessName.trim().length >= BUSINESS_NAME_MIN_LENGTH
  }

  private get _isStreetAddress1Valid() {
    return this._streetAddress1.trim().length >= STREET_ADDRESS_MIN_LENGTH
  }

  private get _isCityValid() {
    return this._city.trim().length > 0
  }

  private get _isStateValid() {
    return this._state.trim().length > 0
  }

  private get _isPostalCodeValid() {
    return POSTAL_CODE_REGEX.test(this._postalCode)
  }

  get isWebsiteUrlValid() {
    return isLink(this._websiteUrl)
  }

  private get _isCompanyTypeValid() {
    return this._companyType.trim().length > 0
  }

  private get _isBusinessIndustryValid() {
    return this._businessIndustry.trim().length > 0
  }

  private get _isStockExchangeValid() {
    return !this.isPublicCorporation || this._stockExchange.trim().length > 0
  }

  private get _isStockSymbolValid() {
    return !this.isPublicCorporation || this._stockSymbol.trim().length > 0
  }

  get params(): IRcsBusinessInfoStepParams {
    return {
      business_phone: this.phoneStore.fullUnformattedNumber,
      company_type: this._companyType,
      business_industry: this._businessIndustry,
      business_name: this._businessName,
      ein: this._ein,
      street_address_1: this._streetAddress1,
      street_address_2: this._streetAddress2 || undefined,
      city: this._city,
      state: this._state,
      address_country: US_COUNTRY_ISO_CODE,
      postal_code: this._postalCode,
      website_url: this._websiteUrl,
      stock_exchange: this.isPublicCorporation ? this._stockExchange : undefined,
      stock_symbol: this.isPublicCorporation ? this._stockSymbol : undefined,
    }
  }

  get isNextDisabled() {
    return !(
      this._isEinValid &&
      this._isBusinessNameValid &&
      this._isStreetAddress1Valid &&
      this._isCityValid &&
      this._isStateValid &&
      this._isPostalCodeValid &&
      this.phoneStore.isValidNumber &&
      this.isWebsiteUrlValid &&
      this._isCompanyTypeValid &&
      this._isBusinessIndustryValid &&
      this._isStockExchangeValid &&
      this._isStockSymbolValid
    )
  }

  setEin = (value: string) => {
    this._ein = this._entities.complianceStore.normalizeEinInput(value)
    if (this._einError) this._einError = ''

    this._appliedEinItem = null
    this._einItems = []
    this._isNoEinResults = false
  }

  setActiveTab = (tab: IBusinessInfoTab) => {
    this._activeTab = tab
  }

  setEinSearch = (value: string) => {
    const digits = this._entities.complianceStore.normalizeEinInput(value)

    if (this._appliedEinItem) this._clearFields()

    this._einError = ''
    this._isNoEinResults = false

    if (digits.length < EIN_MAX_LENGTH) this._einItems = []

    this._ein = digits

    if (digits.length === EIN_MAX_LENGTH) this._searchEin(digits)
  }

  applyEinItem = (einItem: IResponseEinItem) => {
    this._appliedEinItem = einItem
    this._ein = this._entities.complianceStore.normalizeEinInput(einItem.ein)
    this._businessName = einItem.name
    this._streetAddress1 = einItem.street1
    this._streetAddress2 = einItem.street2
    this._city = einItem.city
    this._state = einItem.state
    this._postalCode = formatPostalCode(einItem.zip)

    this._einError = ''
    this._businessNameError = ''
    this._streetAddress1Error = ''
    this._cityError = ''
    this._stateError = ''
    this._postalCodeError = ''

    this._websiteUrl = ''
    this._websiteUrlError = ''
    this._companyType = ''
    this._companyTypeError = ''
    this._businessIndustry = ''
    this._businessIndustryError = ''
    this._stockExchange = ''
    this._stockExchangeError = ''
    this._stockSymbol = ''
    this._stockSymbolError = ''
    this.phoneStore.reset()
  }

  private _searchEin = async (searchedEin: string) => {
    try {
      const { data } = await ComplianceApi.getEinData({ ein: searchedEin })

      if (searchedEin !== this._ein) return

      runInAction(() => {
        this._einItems = data.data
        this._isNoEinResults = data.data.length === 0
      })
    } catch (error) {
      if (searchedEin !== this._ein) return

      runInAction(() => {
        this._einItems = []

        if (error instanceof AxiosError && error.response?.status === 404) {
          this._isNoEinResults = true
          return
        }

        logger.error(error)
        if (error instanceof AxiosError) this._einError = error.errors.getError('message')
      })
    }
  }

  setBusinessName = (value: string) => {
    this._businessName = value
    if (this._businessNameError) this._businessNameError = ''
  }

  setStreetAddress1 = (value: string) => {
    this._streetAddress1 = value
    if (this._streetAddress1Error) this._streetAddress1Error = ''
  }

  setStreetAddress2 = (value: string) => {
    this._streetAddress2 = value
  }

  setCity = (value: string) => {
    this._city = value
    if (this._cityError) this._cityError = ''
  }

  setState = (value: string) => {
    this._state = value
    if (this._stateError) this._stateError = ''
  }

  setPostalCode = (value: string) => {
    this._postalCode = formatPostalCode(value)
    if (this._postalCodeError) this._postalCodeError = ''
  }

  setWebsiteUrl = (value: string) => {
    this._websiteUrl = value
    if (this._websiteUrlError) this._websiteUrlError = ''
  }

  setCompanyType = (value: string) => {
    this._companyType = value
    if (this._companyTypeError) this._companyTypeError = ''
  }

  setBusinessIndustry = (value: string) => {
    this._businessIndustry = value
    if (this._businessIndustryError) this._businessIndustryError = ''
  }

  setStockExchange = (value: string) => {
    this._stockExchange = value
    if (this._stockExchangeError) this._stockExchangeError = ''
  }

  private _normalizeStockSymbol = (value: string) =>
    value.toUpperCase().replace(NON_UPPERCASE_LETTER_REGEX, '').slice(0, STOCK_SYMBOL_MAX_LENGTH)

  setStockSymbol = (value: string) => {
    this._stockSymbol = this._normalizeStockSymbol(value)
    if (this._stockSymbolError) this._stockSymbolError = ''
  }

  loadBusinessReferences = () => {
    this._wizardStore.loadBusinessReferences()
  }

  loadStates = async () => {
    if (this._statesLoading || this.stateOptions.length) return

    runInAction(() => {
      this._statesLoading = true
    })

    try {
      await this._entities.complianceStore.loadBusinessProfileStates()
    } finally {
      runInAction(() => {
        this._statesLoading = false
      })
    }
  }

  loadStockExchanges = async () => {
    if (this._stockExchangesLoading || this._stockExchanges.length) return

    runInAction(() => {
      this._stockExchangesLoading = true
    })

    try {
      const { data } = await ComplianceApi.getStockExchanges()
      runInAction(() => {
        this._stockExchanges = data
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._stockExchangesLoading = false
      })
    }
  }

  validateEin = () => {
    if (!this._ein) {
      this._einError = 'Enter an EIN'
      return
    }
    this._einError = this._isEinValid
      ? ''
      : this._entities.complianceStore.getEinErrorMessageText(US_COUNTRY_ISO_CODE)
  }

  validateBusinessName = () => {
    if (!this._businessName.trim()) {
      this._businessNameError = 'Enter a legal business name'
      return
    }
    this._businessNameError = this._isBusinessNameValid
      ? ''
      : 'Legal business name must be at least 2 characters'
  }

  validateStreetAddress1 = () => {
    if (!this._streetAddress1.trim()) {
      this._streetAddress1Error = 'Enter a business address'
      return
    }
    this._streetAddress1Error = this._isStreetAddress1Valid
      ? ''
      : 'Business address must be at least 2 characters'
  }

  validateCity = () => {
    this._cityError = this._isCityValid ? '' : 'Enter a city'
  }

  validatePostalCode = () => {
    if (!this._postalCode) {
      this._postalCodeError = 'Enter a ZIP code'
      return
    }
    this._postalCodeError = this._isPostalCodeValid ? '' : 'Enter a valid ZIP code'
  }

  validateWebsiteUrl = () => {
    if (!this._websiteUrl) {
      this._websiteUrlError = 'Enter a URL'
      return
    }
    this._websiteUrlError = this.isWebsiteUrlValid ? '' : 'Enter a valid URL'
  }

  validateStockSymbol = () => {
    this._stockSymbolError = this._isStockSymbolValid ? '' : 'Enter a stock ticker symbol'
  }

  init = (data: IResponseRcsCompliance) => {
    if (data.data?.business_phone) this.phoneStore.setFullNumber(data.data.business_phone)
    this._companyType = data.data?.company_type?.key ?? ''
    this._businessIndustry = data.data?.business_industry?.key ?? ''
    if (this._companyType || this._businessIndustry) this.loadBusinessReferences()

    const businessInfo = data.data?.['business-info']
    if (!businessInfo) return

    this._activeTab = 'manual'
    this._ein = this._entities.complianceStore.normalizeEinInput(businessInfo.ein ?? '')
    this._businessName = businessInfo.business_name ?? ''
    this._streetAddress1 = businessInfo.street_address_1 ?? ''
    this._streetAddress2 = businessInfo.street_address_2 ?? ''
    this._city = businessInfo.city ?? ''
    this._state = businessInfo.state ?? ''
    this._postalCode = formatPostalCode(businessInfo.postal_code ?? '')
    this._websiteUrl = businessInfo.website_url ?? ''
    this._stockExchange = businessInfo.stock_exchange ?? ''
    this._stockSymbol = this._normalizeStockSymbol(businessInfo.stock_symbol ?? '')

    if (this.isPublicCorporation && this._stockExchange) this.loadStockExchanges()
  }

  initErrors = (errors: INormalizedApiError) => {
    this._einError = errors.getError('ein')
    this._businessNameError = errors.getError('business_name')
    this._streetAddress1Error = errors.getError('street_address_1')
    this._cityError = errors.getError('city')
    this._stateError = errors.getError('state')
    this._postalCodeError = errors.getError('postal_code')
    this.phoneStore.setNumberRequestError(errors.getError('business_phone'))
    this._websiteUrlError = errors.getError('website_url')
    this._companyTypeError = errors.getError('company_type')
    this._businessIndustryError = errors.getError('business_industry')
    this._stockExchangeError = errors.getError('stock_exchange')
    this._stockSymbolError = errors.getError('stock_symbol')
  }

  private _clearFields = () => {
    this._ein = ''
    this._einError = ''
    this._businessName = ''
    this._businessNameError = ''
    this._streetAddress1 = ''
    this._streetAddress1Error = ''
    this._streetAddress2 = ''
    this._city = ''
    this._cityError = ''
    this._state = ''
    this._stateError = ''
    this._postalCode = ''
    this._postalCodeError = ''
    this._websiteUrl = ''
    this._websiteUrlError = ''
    this._companyType = ''
    this._companyTypeError = ''
    this._businessIndustry = ''
    this._businessIndustryError = ''
    this._stockExchange = ''
    this._stockExchangeError = ''
    this._stockSymbol = ''
    this._stockSymbolError = ''
    this._einItems = []
    this._appliedEinItem = null
    this._isNoEinResults = false
    this.phoneStore.reset()
  }

  reset = () => {
    this._clearFields()
    this._activeTab = 'find'
  }
}
